import React, { useState, useEffect } from 'react';
import { calcBatchBody } from '@carboncare/schemas';
import { preflight } from '../services/api/schemaPreflight';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PlayArrow as CalculateIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { fileService, ProcessedFile } from '../services/fileService';
import LicenseeFilter from '../components/common/LicenseeFilter';
import PageHeader from '../components/common/PageHeader';
import { apiClient } from '../services/api/authApi';
import { AccountType } from '../types/auth';
import { getUserTypeLabel, formatFileSize, makeHandleSort } from './_shared/fileUtils';
import {
  SortableTableHeaderCell,
  FilesTableContainer,
  FilesTableFooter,
  FileNameCell,
  DeleteIconButton,
} from './_shared/FilesTableShared';

// ProcessedFile interface is now imported from fileService

const ProcessedFilesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [files, setFiles] = useState<ProcessedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<ProcessedFile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [calculateDialogOpen, setCalculateDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterCompanyId, setFilterCompanyId] = useState<number | ''>('');
  const [filterCompanyName, setFilterCompanyName] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFiles, setTotalFiles] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState<'processedAt' | 'fileName'>('processedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;

  // Mirrors `files` so the polling effect and the progress fallback can read the
  // current list without taking a dependency on it (which would rebuild the timer
  // on every refresh).
  const filesRef = React.useRef<ProcessedFile[]>([]);
  const requestCounter = React.useRef(0);
  const latestRequestId = React.useRef(0);
  useEffect(() => { filesRef.current = files; }, [files]);

  const handleSort = makeHandleSort(sortBy, setSortBy, setSortDirection, setPage);

  const fetchFiles = async (currentPage = page, currentLimit = rowsPerPage, search = searchTerm, companyName = filterCompanyName, currentSortBy = sortBy, currentSortDirection = sortDirection, status = filterStatus) => {
    const requestId = ++requestCounter.current;
    try {
      setLoading(true);
      const result = await fileService.getProcessedFiles({
        page: currentPage + 1,
        limit: currentLimit,
        search: search || undefined,
        companyName: companyName || undefined,
        sortBy: currentSortBy,
        sortDirection: currentSortDirection,
        status: status || undefined,
      });

      // Update files with progress from backend
      const updatedFiles = await Promise.all(result.data.map(async (file) => {
        if (file.status === 'processing' && file.id) {
          try {
            const progressResponse = await apiClient.get(`/xml/calc/progress/${file.id}`);
            if (progressResponse.data.success) {
              const progressData = progressResponse.data.data;
              return {
                ...file,
                progress: progressData.percentComplete || 0,
                rowsProcessed: progressData.completed || 0,
                totalRows: progressData.total || file.totalRows,
                status: (() => {
                  if (progressData.status === 'completed') return 'calculated' as const;
                  if (progressData.status === 'failed') return 'error' as const;
                  return 'processing' as const;
                })(),
              };
            }
          } catch (error) {
            console.error('Progress not available for file:', file.id, error);
          }
          // Progress lives in the API's memory; the DB columns on this row read 0
          // until the run flushes them. Falling through to the row would snap the
          // bar back to 0 on any hiccup, so keep whatever we last displayed.
          const previous = filesRef.current.find(f => f.id === file.id);
          return {
            ...file,
            progress: previous?.progress ?? file.progress ?? 0,
            rowsProcessed: previous?.rowsProcessed ?? file.rowsProcessed ?? 0,
          };
        }
        return file;
      }));

      // Responses can land out of order (each fetch issues 1 + N requests). Drop
      // anything older than what we have already rendered.
      if (requestId < latestRequestId.current) return;
      latestRequestId.current = requestId;

      setFiles(updatedFiles);
      setTotalFiles(result.pagination.total);
    } catch (error) {
      console.error('Error fetching processed files:', error);
      if (files.length === 0) {
        setError('Failed to load processed files');
      }
    } finally {
      setLoading(false);
    }
  };

  const prevSearch = React.useRef(searchTerm);
  const prevCompany = React.useRef(filterCompanyName);
  const prevStatus = React.useRef(filterStatus);

  useEffect(() => {
    const searchChanged = prevSearch.current !== searchTerm;
    const companyChanged = prevCompany.current !== filterCompanyName;
    const statusChanged = prevStatus.current !== filterStatus;
    prevSearch.current = searchTerm;
    prevCompany.current = filterCompanyName;
    prevStatus.current = filterStatus;

    if (searchChanged) {
      const timer = setTimeout(() => {
        setPage(0);
        fetchFiles(0, rowsPerPage, searchTerm, filterCompanyName);
      }, 600);
      return () => clearTimeout(timer);
    }

    if (companyChanged || statusChanged) {
      setPage(0);
      fetchFiles(0, rowsPerPage, searchTerm, filterCompanyName);
      return;
    }

    fetchFiles();
  }, [user, page, rowsPerPage, searchTerm, filterCompanyName, filterStatus, sortBy, sortDirection]);

  // Polling for progress updates. Reads the list through a ref: depending on
  // `files` tore down and recreated the interval on every refresh.
  useEffect(() => {
    const interval = setInterval(() => {
      if (filesRef.current.some(f => f.status === 'processing')) {
        fetchFiles();
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued': return 'default';
      case 'processing': return 'warning';
      case 'ready': return 'success';
      case 'calculated': return 'primary';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready': return <CheckCircleIcon fontSize="small" />;
      case 'queued': return <ScheduleIcon fontSize="small" />;
      default: return undefined;
    }
  };

  const handleDownload = async (file: ProcessedFile) => {
    try {
      await fileService.downloadFile(file.id);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    }
  };

  const handleDelete = async (file: ProcessedFile) => {
    try {
      await fileService.deleteFile(file.id);
      setDeleteDialogOpen(false);
      setSelectedFile(null);
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file');
    }
  };

  const handleCalculate = async (file: ProcessedFile) => {
    try {
      console.log('Starting calculation for file:', file.fileName);

      // Call backend to start calculation via batch endpoint
      const batchBody = { fileId: file.id };
      preflight(calcBatchBody, batchBody);
      const response = await apiClient.post('/xml/calc/batch', batchBody);

      if (response.data.success) {
        // Update file status to processing
        setFiles(prev => prev.map(f =>
          f.id === file.id ? { ...f, status: 'processing' as const, progress: 0 } : f
        ));

        // Start polling for progress immediately
        setTimeout(() => fetchFiles(), 500);
      } else {
        throw new Error(response.data.message || 'Failed to start calculation');
      }

      setCalculateDialogOpen(false);
      setSelectedFile(null);
    } catch (error: any) {
      console.error('Error starting calculation:', error);
      setError(error.message || 'Failed to start calculation');
    }
  };

  return (
    <Box>
      <PageHeader description="Files currently being validated and calculated." />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Search files"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 0 }}
                InputLabelProps={{
                  sx: { mt: '6px' }
                }}
              />
            </Grid>
            {isMasterAdmin && (
              <Grid item xs={12} sm={3}>
                <LicenseeFilter
                  value={filterCompanyId}
                  onChange={(id, name) => {
                    setFilterCompanyId(id);
                    setFilterCompanyName(name);
                  }}
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small" sx={{ mb: 0 }}>
                <InputLabel sx={{ mt: '6px' }}>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="queued">Queued</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                {totalFiles} file{totalFiles === 1 ? '' : 's'} total
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Files Table */}
      <FilesTableContainer loading={loading}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell
                columnKey="fileName"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                defaultDirection="asc"
              >
                File Name
              </SortableTableHeaderCell>
              <TableCell>Schema</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Status</TableCell>
              {user?.accountType && user?.accountType >= AccountType.CarbonCareAdmin && <TableCell>Licensee</TableCell>}
              <TableCell>Processed By</TableCell>
              <SortableTableHeaderCell
                columnKey="processedAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                defaultDirection="desc"
              >
                Process Date
              </SortableTableHeaderCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id}>
                <FileNameCell
                  name={file.originalName}
                  subtitle={`${formatFileSize(file.fileSize)} • ${file.totalRows} rows`}
                />
                <TableCell>
                  <Box>
                    <Typography variant="body2">{file.schema}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {file.settings.standard} •
                      {file.settings.hasHeader ? ' Headers' : ' No Headers'} •
                      {file.settings.useStreetDistances ? ' Street Distances' : ' Direct Distances'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ width: 120 }}>
                    <LinearProgress
                      variant={file.status === 'processing' ? 'indeterminate' : 'determinate'}
                      value={file.progress || 0}
                      sx={{ mb: 0.5 }}
                      color={(() => {
                        if (file.status === 'error') return 'error';
                        if (file.status === 'calculated') return 'success';
                        return 'primary';
                      })()}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption">
                        {file.progress || 0}%
                      </Typography>
                      {file.rowsProcessed !== undefined && file.totalRows && (
                        <Typography variant="caption" color="text.secondary">
                          {file.rowsProcessed}/{file.totalRows}
                        </Typography>
                      )}
                    </Box>
                    {file.estimatedTime && (
                      <Typography variant="caption" color="text.secondary">
                        ~{file.estimatedTime} remaining
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={file.status.charAt(0).toUpperCase() + file.status.slice(1)}
                    color={getStatusColor(file.status) as any}
                    size="small"
                    icon={getStatusIcon(file.status)}
                  />
                </TableCell>
                {user?.accountType && user?.accountType >= AccountType.CarbonCareAdmin && (
                  <TableCell>{file.companyName}</TableCell>
                )}
                <TableCell>
                  <Box>
                    <Typography variant="body2">{file.processedBy?.name || 'Unknown User'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getUserTypeLabel(file.userType)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {file.processedAt ?
                    `${file.processedAt.toLocaleDateString()} ${file.processedAt.toLocaleTimeString()}` :
                    'Not processed'
                  }
                </TableCell>
                <TableCell align="right">
                  {file.status === 'error' && (
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(file)}
                      title="Download Original File"
                      color="primary"
                    >
                      <DownloadIcon />
                    </IconButton>
                  )}
                  {(file.status === 'ready' || file.status === 'queued') && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedFile(file);
                          setCalculateDialogOpen(true);
                        }}
                        title="Start Calculation"
                        color="success"
                      >
                        <CalculateIcon />
                      </IconButton>
                    )}
                  <DeleteIconButton
                    onClick={() => {
                      setSelectedFile(file);
                      setDeleteDialogOpen(true);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </FilesTableContainer>

      <FilesTableFooter
        total={totalFiles}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        loading={loading}
        filesLength={files.length}
        emptyPrimaryText="No processed files found"
        emptySecondaryText="No files have been processed yet."
        deleteDialogOpen={deleteDialogOpen}
        onDeleteClose={() => setDeleteDialogOpen(false)}
        onDeleteConfirm={() => selectedFile && handleDelete(selectedFile)}
        deleteTitle="Delete Processed File"
        deleteMessage={`Are you sure you want to delete "${selectedFile?.originalName}"? This will remove the processed file and any calculation results.`}
      />

      {/* Calculate Confirmation Dialog */}
      <Dialog open={calculateDialogOpen} onClose={() => setCalculateDialogOpen(false)}>
        <DialogTitle>Start Calculation</DialogTitle>
        <DialogContent>
          <Typography>
            Start carbon calculation for "{selectedFile?.originalName}"?
            This will begin the calculation process and may take several minutes.
          </Typography>
          {selectedFile?.totalRows && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing {selectedFile.totalRows} rows with {selectedFile.schema} schema.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalculateDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button
            onClick={() => selectedFile && handleCalculate(selectedFile)}
            color="primary"
            variant="contained"
          >
            Start Calculation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessedFilesPage;